import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

/**
 * Rodapé padronizado para formulários em modal.
 * Exibe botões Cancelar e Salvar com estado de loading.
 *
 * Uso:
 * ```html
 * <app-form-footer
 *   [salvando]="salvando"
 *   [podeSubmeter]="formularioValido"
 *   [labelSalvar]="'SALVAR'"
 *   [labelSalvando]="'SALVANDO...'"
 *   (cancelar)="fechar()"
 *   (salvar)="onSubmit()">
 * </app-form-footer>
 * ```
 */
@Component({
  selector: 'app-form-footer',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="flex shrink-0 flex-col gap-[10px] border-t border-gray-200 px-[0.95rem] pb-[0.95rem] pt-[0.75rem] dark:border-gray-700">
      <ng-container *ngIf="mostrarExcluir; else doisBotoes">
        <button
          type="submit"
          [disabled]="!podeSubmeter || salvando"
          [ngClass]="podeSubmeter && !salvando ? 'bg-[#3b5dcc] hover:bg-[#2e4ea8] cursor-pointer' : 'bg-[#9ab7ee] cursor-not-allowed'"
          class="h-[2.16rem] rounded-[0.25rem] text-[13px] font-medium tracking-[0.08em] text-white transition-colors inline-flex items-center justify-center"
          (click)="!salvando && podeSubmeter && salvar.emit()">
          <span class="inline-flex items-center justify-center gap-2 whitespace-nowrap">
            <svg *ngIf="salvando" class="h-4 w-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ salvando ? labelSalvando : labelSalvar }}
          </span>
        </button>
        <button
          type="button"
          (click)="excluir.emit()"
          [disabled]="salvando"
          class="h-[2.16rem] rounded-[0.25rem] border border-[#c0cad8] bg-white text-[13px] font-medium tracking-[0.08em] text-gray-700 hover:bg-gray-50 transition-colors dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5">
          <lucide-icon name="trash-2" class="h-4 w-4"></lucide-icon>
          EXCLUIR
        </button>
        <button
          type="button"
          (click)="cancelar.emit()"
          [disabled]="salvando"
          class="h-[2.16rem] rounded-[0.25rem] border border-[#c0cad8] bg-white text-[13px] font-medium tracking-[0.08em] text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
          {{ labelCancelar }}
        </button>
      </ng-container>
      <ng-template #doisBotoes>
        <div class="grid grid-cols-2 gap-[0.54rem]">
          <button
            type="submit"
            [disabled]="!podeSubmeter || salvando"
            [ngClass]="podeSubmeter && !salvando ? 'bg-[#3b5dcc] hover:bg-[#2e4ea8] cursor-pointer' : 'bg-[#9ab7ee] cursor-not-allowed'"
            class="h-[2.16rem] rounded-[0.25rem] text-[13px] font-medium tracking-[0.08em] text-white transition-colors"
            (click)="!salvando && podeSubmeter && salvar.emit()">
            <span class="inline-flex items-center justify-center gap-2">
              <svg *ngIf="salvando" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ salvando ? labelSalvando : labelSalvar }}
            </span>
          </button>
          <button
            type="button"
            (click)="cancelar.emit()"
            [disabled]="salvando"
            class="h-[2.16rem] rounded-[0.25rem] border border-[#c0cad8] bg-white text-[13px] font-medium tracking-[0.08em] text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
            {{ labelCancelar }}
          </button>
        </div>
      </ng-template>
    </div>
  `
})
export class FormFooterComponent {
  @Input() salvando = false;
  @Input() podeSubmeter = true;
  @Input() labelCancelar = 'CANCELAR';
  @Input() labelSalvar = 'SALVAR';
  @Input() labelSalvando = 'SALVANDO...';
  @Input() mostrarExcluir = false;

  @Output() cancelar = new EventEmitter<void>();
  @Output() salvar = new EventEmitter<void>();
  @Output() excluir = new EventEmitter<void>();
}
